import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ItemWithGroup,
  useAssigneePicker,
  UseAssigneePickerProps,
} from '@adsk/alloy-assignee-picker';
import Dropdown, {
  AutocompleteControl,
  DropdownProps,
  MultiAutocompleteTokenControl,
  Tokens,
  VirtualizedMenu,
  VirtualizedMenuItem,
} from '@adsk/alloy-react-dropdown';
import Tooltip from '@adsk/alloy-react-tooltip';
import Avatar from '@adsk/alloy-react-avatar';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import { FooterProps } from '@adsk/alloy-react-dropdown/es/types';
import Portal from '@adsk/alloy-react-portal';
import { MultiAutocompleteControlProps } from '@adsk/alloy-react-dropdown/es/templates/Control/MultiAutocompleteControl';
import { DEFAULT_STATUS_FILTERS } from '@adsk/alloy-assignee-picker/es/consts';
import { AutoCompleteControlProps } from '@adsk/alloy-react-dropdown/es/templates/Control/AutocompleteControl';
import { ALL_USERS_SPECIAL_ID, EMPTY_ARRAY, EMPTY_OBJECT, HQUserStatus } from '../../../constants';
import { translate } from '../../../helpers';
import { Assignee, AssigneeType } from '../../../sdk';
import { useUsers } from '../../../providers/RFIsUsersProvider';
import AssigneeAvatar from '../../AssigneeAvatar';

const STATUS_FILTERS: UseAssigneePickerProps['statusFilters'] = {
  ...DEFAULT_STATUS_FILTERS,
  users: ['active', 'pending', 'disabled', 'deleted', 'not_invited'],
};

const FULL_WIDTH_STYLE = { width: '100%' };
const AVATAR_STYLE = {
  marginRight: 8,
  fontWeight: 500,
};

type FooterRenderer = (
  props: FooterProps & {
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    loading: boolean;
    setSelectedItems: (items: ItemWithGroup[]) => void;
    isOpen: boolean;
  },
) => ReactNode;

export type AssigneePickerProps = {
  selectPlaceholder?: string;
  searchPlaceholder?: string;
  assigneesOptions: Assignee[];
  selectedAssignees: Assignee[];
  disabledAssignees?: Assignee[];
  // Returns the tooltip text for a disabled assignee. The picker's default tooltip (e.g. "Answered")
  // is passed in, so callers return it for assignees they don't want to override and return their
  // own text for the ones they do (e.g. "Already selected" for an other-pool conflict). Defaults to
  // returning the picker's default for every assignee.
  getAssigneeTooltip?: (assignee: Assignee, defaultTooltip: string) => string;
  maxAssignees?: number;
  renderVirtualizedMenuFooter?: FooterRenderer;
  onChange?: (value: Assignee[]) => void;
  isOpen?: boolean;
  onClose?: () => void;
  hideDeletedUsers?: boolean;
  clearable?: boolean;
  maxMenuHeight?: number;
} & DropdownProps<ItemWithGroup>;

const mapDropdownItemToSelectedAssignee = (item: ItemWithGroup): Assignee => ({
  type: item.group as AssigneeType,
  id: item.value,
});

const TOKENS_STYLE = { flex: 'initial', maxHeight: 'unset' };

type MultiAutocompleteControlPropsArgs = Parameters<
  NonNullable<MultiAutocompleteControlProps<ItemWithGroup>['renderItems']>
>[0];

const itemToString = (item: ItemWithGroup | null) => {
  if (item?.group === 'user' && item?.subtext) {
    return `${item.label} (${item.subtext})`;
  }

  return (item?.label as string) || '';
};

const AssigneePickerControlTokens = (props: MultiAutocompleteControlPropsArgs) => (
  <Tokens<ItemWithGroup>
    {...props}
    style={TOKENS_STYLE}
    itemToString={itemToString}
    renderLeftComponent={(item) => (
      <AssigneeAvatar
        style={AVATAR_STYLE}
        size={Avatar.SIZES.SMALL_16}
        rolesAndCompaniesIconSize={12}
        assignee={{ id: item.value as string, type: item.group as AssigneeType }}
      />
    )}
  />
);

export const renderMultiControl: DropdownProps<ItemWithGroup>['renderControl'] = (props: any) => (
  <MultiAutocompleteTokenControl<ItemWithGroup>
    renderItems={(renderItemsProps) => <AssigneePickerControlTokens {...renderItemsProps} />}
    {...props}
    style={{ ...props.style, ...FULL_WIDTH_STYLE }}
  />
);

export const renderSingleControl: DropdownProps<ItemWithGroup>['renderControl'] = (
  props: AutoCompleteControlProps<ItemWithGroup>,
) => (
  <AutocompleteControl<ItemWithGroup>
    {...props}
    style={{ ...props.style, ...FULL_WIDTH_STYLE }}
    itemToString={itemToString}
  />
);

export const AssigneePicker: FC<AssigneePickerProps> = ({
  assigneesOptions = EMPTY_OBJECT,
  selectedAssignees,
  disabledAssignees,
  maxAssignees,
  getAssigneeTooltip = (_assignee, defaultTooltip) => defaultTooltip,
  onChange,
  renderVirtualizedMenuFooter,
  selectPlaceholder,
  searchPlaceholder,
  isOpen = false,
  onClose,
  hideDeletedUsers = true,
  clearable = false,
  maxMenuHeight = 423,
  ...baseProps
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(isOpen);
  useEffect(() => {
    setIsMenuOpen(isOpen);
  }, [isOpen]);

  const assigneeOptionsDict = useMemo(
    () => keyBy(assigneesOptions as Assignee[], 'id'),
    [assigneesOptions],
  );

  const selectedAssigneesDict = useMemo(() => keyBy(selectedAssignees, 'id'), [selectedAssignees]);
  const optionsFilter = useMemo(
    (): UseAssigneePickerProps['optionsFilter'] => ({
      user: (value) => {
        if (hideDeletedUsers && value.status === HQUserStatus.DELETED) {
          return value.autodeskId in selectedAssigneesDict;
        }

        return (
          ALL_USERS_SPECIAL_ID in assigneeOptionsDict || value.autodeskId in assigneeOptionsDict
        );
      },
      company: (value) => value.memberGroupId in assigneeOptionsDict,
      role: (value) => value.memberGroupId in assigneeOptionsDict,
    }),
    [assigneeOptionsDict, hideDeletedUsers, selectedAssigneesDict],
  );

  const rfiUsers = useUsers();
  const { dropdownProps } = useAssigneePicker({
    optionsFilter,
    customUsers: rfiUsers,
    statusFilters: STATUS_FILTERS,
  });

  const isRolesAndCompanies = useMemo(
    () =>
      Object.values(assigneeOptionsDict).some(
        (assignee) => assignee.type === AssigneeType.Company || assignee.type === AssigneeType.Role,
      ),
    [assigneeOptionsDict],
  );

  const isMultipleSelection = !maxAssignees || maxAssignees > 1;

  let maxAssigneesReached: boolean;
  const selectedAssigneesCount = selectedAssignees.length;
  if (isMultipleSelection) {
    maxAssigneesReached = !!maxAssignees && selectedAssigneesCount >= maxAssignees;
  } else {
    maxAssigneesReached = selectedAssigneesCount === 1;
  }

  const getItemDisabledInfo = useCallback(
    (item: ItemWithGroup): { disabled: boolean; tooltip: string } => {
      const disabledMatch = disabledAssignees?.find(({ id }) => item.value === id);
      if (disabledMatch) {
        return {
          disabled: true,
          tooltip: getAssigneeTooltip(disabledMatch, translate('assigneePicker.answeredTooltip')),
        };
      }

      if (maxAssignees === 1 || !maxAssigneesReached) {
        return {
          disabled: false,
          tooltip: '',
        };
      }

      const isItemSelected = selectedAssignees.some((a) => a.id === item.value);
      return {
        disabled: !isItemSelected,
        tooltip: isItemSelected
          ? ''
          : translate('assigneePicker.maxMembersTooltip', { max: maxAssignees }),
      };
    },
    [disabledAssignees, getAssigneeTooltip, maxAssignees, maxAssigneesReached, selectedAssignees],
  );

  const handleMultipleOnChange = ({ selectedItems }: { selectedItems: ItemWithGroup[] }) => {
    if (isEmpty(selectedItems)) {
      onChange?.([]);
      return;
    }

    const assignees = selectedItems.map(mapDropdownItemToSelectedAssignee);

    onChange?.(assignees);
  };

  const handleSingleOnChange = ({ selectedItem }: { selectedItem: ItemWithGroup }) => {
    if (isEmpty(selectedItem)) {
      onChange?.([]);
      return;
    }

    const assignees: Assignee[] = [mapDropdownItemToSelectedAssignee(selectedItem)];

    onChange?.(assignees);
  };

  const renderMenu: DropdownProps<ItemWithGroup>['renderMenu'] = (menuProps, virtualProps) => (
    <Portal container={document.body}>
      {VirtualizedMenu.render(
        {
          ...menuProps,
          style: { ...menuProps.style, maxHeight: maxMenuHeight, minWidth: 320 },
          // Clearable is the default behavior of the dropdown
          ...(!clearable && {
            renderFooter: (props) =>
              renderVirtualizedMenuFooter?.({
                ...props,
                setIsOpen: setIsMenuOpen,
                isOpen: isMenuOpen,
                setSelectedItems: (selectedItems: ItemWithGroup[]) => {
                  handleMultipleOnChange({ selectedItems });
                },
                loading: rfiUsers.loading,
              }) || null,
          }),
          renderMenuItem: (item) => {
            const menuItem = item.item as unknown as ItemWithGroup;
            const tooltip = item.disabled ? getItemDisabledInfo(menuItem).tooltip : '';

            return (
              <Tooltip
                key={menuItem.value || (menuItem.label as string)}
                content={tooltip}
                placement={Tooltip.PLACEMENTS.BOTTOM}
                offset={0}
                getTargetProps={() => ({
                  shouldWrapChildren: false,
                  ...item,
                  renderLeftComponent: () => (
                    <AssigneeAvatar
                      style={AVATAR_STYLE}
                      size={Avatar.SIZES.MEDIUM_24}
                      assignee={{ id: menuItem.value, type: menuItem.group as AssigneeType }}
                    />
                  ),
                })}
              >
                <VirtualizedMenuItem />
              </Tooltip>
            );
          },
        },
        virtualProps,
      )}
    </Portal>
  );

  const getSelectPlaceholder = () => {
    if (selectPlaceholder) {
      return selectPlaceholder;
    }

    if (!isMultipleSelection && !isRolesAndCompanies) {
      return translate('assigneePicker.selectPlaceholderSingle');
    }

    if (!isMultipleSelection && isRolesAndCompanies) {
      return translate('assigneePicker.selectMemberRoleCompany');
    }

    if (maxAssignees && isRolesAndCompanies) {
      return translate('assigneePicker.selectMultiplePlaceholderRolesCompanies', { maxAssignees });
    }

    if (maxAssignees) {
      return translate('assigneePicker.selectMultiplePlaceholder', { maxAssignees });
    }

    if (isRolesAndCompanies) {
      return translate('assigneePicker.selectMemberRoleCompanyPlural');
    }

    return translate('assigneePicker.selectMembers');
  };

  const getSearchPlaceholder = () => {
    if (searchPlaceholder) {
      return searchPlaceholder;
    }

    if (isRolesAndCompanies) {
      return translate('assigneePicker.searchMembersRolesCompanies');
    }

    return translate('assigneePicker.searchPlaceholder');
  };

  const groupedItems = useMemo(
    () =>
      dropdownProps.groupedItems.map((group) => ({
        ...group,
        items: group.items.map((item) => ({
          ...item,
          disabled: getItemDisabledInfo(item).disabled,
        })),
      })),
    [dropdownProps.groupedItems, getItemDisabledInfo],
  );

  const optionsItemsByValue = useMemo(() => {
    const items = groupedItems.flatMap((groups) => groups.items);
    return keyBy(items, 'value');
  }, [groupedItems]);

  const hasMultipleGroups = groupedItems.filter((group) => group.items.length > 0).length > 1;

  const selectedItems = useMemo(
    () =>
      selectedAssignees?.map(({ id }) => optionsItemsByValue[id]).filter(Boolean) || EMPTY_ARRAY,
    [optionsItemsByValue, selectedAssignees],
  );

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <Dropdown<ItemWithGroup>
      style={FULL_WIDTH_STYLE}
      isMultiple={isMultipleSelection}
      {...(hasMultipleGroups && { allowCollapsibleGroups: true })}
      {...dropdownProps}
      groupedItems={groupedItems}
      renderMenu={renderMenu}
      renderControl={isMultipleSelection ? renderMultiControl : renderSingleControl}
      placeholder={isMenuOpen ? getSearchPlaceholder() : getSelectPlaceholder()}
      isOpen={isMenuOpen}
      {...(isMultipleSelection
        ? { selectedItems, onSelectedItemsChange: handleMultipleOnChange }
        : { selectedItem: selectedItems[0] || null, onSelectedItemChange: handleSingleOnChange })}
      onIsOpenChange={({ isOpen: _isOpen = false }) => {
        if (_isOpen === false) {
          onClose?.();
        }

        setIsMenuOpen(_isOpen);
      }}
      {...baseProps}
    />
  );
};
